import { useCallback, useEffect, useState } from "react";
import { BarChart3, Download, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  exportProfitabilityXlsx,
  fetchProfitability,
  fetchProfitabilityCustomers,
  fetchProfitabilityNegotiations,
  fetchProfitabilityUnits,
  REASON_LABELS,
  type CustomerOption,
  type NegotiationOption,
  type ProfitabilityResponse,
  type ProfitabilityRow,
  type UnitOption,
} from "@/lib/adminProfitabilityApi";
import { brToday } from "@/lib/brDate";
import { InfoHint } from "@/components/admin/InfoHint";

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const money = (v: number | null) => (v == null ? "—" : brl.format(v));
// Valor monetário acompanhado da sua unidade (ex.: "R$ 2,36 / Kg"). Unidade ausente → só o valor.
// Zero cadastrado é exibido normalmente (R$ 0,00); ausência (null) é tratada por quem chama (omite a linha).
const moneyWithUnit = (v: number | null, unit: string | null) =>
  v == null ? "—" : `${brl.format(v)}${unit ? ` / ${unit}` : ""}`;
const pct = (v: number | null) => (v == null ? "—" : `${Number(v)}%`);
// Margem exibida em formato brasileiro com 2 casas (ex.: 38,00%). A precisão interna e a classificação
// (ACIMA_META/ENTRE/ABAIXO_MINIMA) vêm do backend pelo valor NÃO arredondado — aqui é só apresentação.
const marginFmt = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const marginPct = (v: number | null) => (v == null ? "—" : `${marginFmt.format(Number(v))}%`);
const SIZE = 50;

const statusStyle: Record<string, string> = {
  ACIMA_META: "bg-primary/10 text-primary",
  ENTRE: "bg-warning/15 text-warning",
  ABAIXO_MINIMA: "bg-destructive/15 text-destructive",
};
const statusLabel: Record<string, string> = {
  ACIMA_META: "Acima da meta", ENTRE: "Entre mín. e meta", ABAIXO_MINIMA: "Abaixo da mínima",
};

export default function AdminProfitability() {
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [units, setUnits] = useState<UnitOption[]>([]);
  const [negotiations, setNegotiations] = useState<NegotiationOption[]>([]);
  const [customerId, setCustomerId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [negotiationId, setNegotiationId] = useState("");
  const [date, setDate] = useState(() => brToday());
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState<ProfitabilityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchProfitabilityCustomers().then(setCustomers).catch((e) =>
      toast.error(e instanceof Error ? e.message : "Falha ao carregar clientes"));
  }, []);

  useEffect(() => {
    setUnitId("");
    setUnits([]);
    if (customerId) {
      fetchProfitabilityUnits(customerId).then(setUnits).catch(() => setUnits([]));
    }
  }, [customerId]);

  // Carrega negociações selecionáveis (cliente + unidade): futura, vigente ou encerrada.
  useEffect(() => {
    setNegotiationId("");
    setNegotiations([]);
    if (customerId) {
      fetchProfitabilityNegotiations(customerId, unitId || null).then(setNegotiations).catch(() => setNegotiations([]));
    }
  }, [customerId, unitId]);

  const load = useCallback(async (toPage: number) => {
    if (!customerId) return;
    setLoading(true);
    try {
      const r = await fetchProfitability({
        customerId, unitId: unitId || null, date, negotiationId: negotiationId || null,
        query: query.trim(), page: toPage, size: SIZE,
      });
      setData(r);
      setPage(toPage);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao carregar a rentabilidade");
    } finally {
      setLoading(false);
    }
  }, [customerId, unitId, date, negotiationId, query]);

  // Exporta o XLSX com os MESMOS filtros atuais (cliente/unidade/negociação/data/busca). A planilha cobre
  // TODO o resultado do filtro (não só a página visível) e usa os mesmos cálculos do backend.
  const exportXlsx = useCallback(async () => {
    if (!customerId) return;
    setExporting(true);
    try {
      await exportProfitabilityXlsx({
        customerId, unitId: unitId || null, date, negotiationId: negotiationId || null, query: query.trim(),
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao exportar o XLSX");
    } finally {
      setExporting(false);
    }
  }, [customerId, unitId, date, negotiationId, query]);

  const neg = data?.negotiation;
  const totalPages = data ? Math.max(1, Math.ceil(data.totalRows / data.size)) : 1;

  return (
    <>
      <section className="bg-gradient-hero text-primary-foreground">
        <div className="container py-8">
          <h1 className="font-display text-3xl font-bold tracking-tight">Rentabilidade</h1>
          <p className="mt-1 text-sm text-primary-foreground/80">
            Composição consultiva de custo, negociação e preço por cliente/unidade. Não altera preços nem operações.
          </p>
        </div>
      </section>

      <div className="container space-y-5 py-6">
        {/* filtros */}
        <div className="grid gap-3 md:grid-cols-5">
          <div className="space-y-1">
            <Label className="text-xs">Cliente</Label>
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}
              className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
              <option value="">Selecione…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Unidade de entrega (opcional)</Label>
            <select value={unitId} onChange={(e) => setUnitId(e.target.value)} disabled={!customerId}
              className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
              <option value="">Cliente (sem unidade)</option>
              {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Negociação</Label>
            <select value={negotiationId} onChange={(e) => setNegotiationId(e.target.value)} disabled={!customerId}
              className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
              <option value="">Atual (estado vigente)</option>
              {negotiations.map((n) => (
                <option key={n.conditionId} value={n.conditionId}>
                  {n.label}{n.hasApprovedTable ? ` · tabela v${n.approvedVersionNumber}` : " · sem tabela"}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Data {negotiationId ? "(usa a vigência da negociação)" : "da negociação"}</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10" disabled={!!negotiationId} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Buscar produto</Label>
            <div className="flex gap-2">
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="nome ou código"
                className="h-10" onKeyDown={(e) => { if (e.key === "Enter") void load(0); }} />
              <Button variant="hero" aria-label="Buscar" disabled={!customerId || loading} onClick={() => void load(0)}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>

        {customerId && (
          <div className="flex justify-end">
            <Button variant="outline" size="sm" disabled={exporting || loading} onClick={() => void exportXlsx()}>
              {exporting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />}
              Exportar XLSX
            </Button>
          </div>
        )}

        {data && (
          <>
            <div className="space-y-1 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
              <div>{data.dataCaveat}</div>
              <div>
                O relatório inclui somente combinações com <strong>preço vendável</strong>. A ausência de uma
                linha não significa custo coberto nem rentabilidade aprovada.
              </div>
            </div>

            {/* contexto: cliente/unidade/negociação/vigência/tabela em uso */}
            {data.selectedNegotiationId && (
              <div className="surface-card p-3 text-xs">
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  <span>Cliente: <strong>{data.customerName}</strong></span>
                  {data.unitName && <span>Unidade: <strong>{data.unitName}</strong></span>}
                  <span>Negociação: <strong>{data.negotiationScope === "UNIDADE_ENTREGA" ? "Unidade de entrega" : "Cliente"}</strong></span>
                  <span>Vigência: <strong>{data.negotiationValidFrom} → {data.negotiationValidTo ?? "em aberto"}</strong>
                    {data.negotiationStatus ? ` (${data.negotiationStatus})` : ""}</span>
                  <span>Tabela: <strong>{data.priceTableRegistered ? `v${data.priceTableVersionNumber} (congelada)` : "não registrada"}</strong></span>
                  <span>Origem dos preços: <strong>{data.priceSource === "NEGOCIACAO_CONGELADA" ? "versão da negociação" : "estado atual"}</strong></span>
                </div>
              </div>
            )}

            {data.selectedNegotiationId && !data.priceTableRegistered && (
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-3 text-sm text-destructive">
                {data.dataCaveat || "Tabela de preços da negociação não registrada."}
                <div className="mt-1 text-xs">
                  Esta negociação não tem versão de tabela congelada. Não há rentabilidade histórica definitiva
                  (os preços atuais não são usados para reconstruí-la).
                </div>
              </div>
            )}

            {/* resumo da negociação */}
            <div className="surface-card p-4 text-sm">
              <div className="mb-2 font-semibold">Negociação — {data.customerName}{data.unitName ? ` / ${data.unitName}` : ""}</div>
              {!neg?.configured ? (
                <div className="text-muted-foreground">{neg?.message ?? "Negociação não cadastrada"}</div>
              ) : (
                <div className="grid gap-1 md:grid-cols-2">
                  <span>Comissão do cliente: <strong>{pct(neg.clientCommissionPercent.value)}</strong></span>
                  <span>Comissão do intermediário: <strong>{pct(neg.intermediaryCommissionPercent.value)}</strong>
                    {neg.intermediaryCommissionBase ? ` (base ${neg.intermediaryCommissionBase === "APOS_COMISSAO_CLIENTE" ? "após comissão do cliente" : "preço final"})` : ""}</span>
                  <span>Margem desejada: <strong>{pct(neg.targetMarginPercent.value)}</strong></span>
                  <span>Margem mínima: <strong>{pct(neg.minimumMarginPercent.value)}</strong></span>
                </div>
              )}
              {data.orderDeliveryExpenses.length > 0 && (
                <div className="mt-2 rounded bg-secondary/40 px-2 py-1 text-xs">
                  Despesas por pedido/entrega (fora da margem unitária):{" "}
                  {data.orderDeliveryExpenses.map((e, i) => (
                    <span key={i}>{i > 0 ? "; " : ""}{e.description} {money(e.fixedValue)} ({e.scope === "POR_PEDIDO" ? "por pedido" : "por entrega"})</span>
                  ))}
                </div>
              )}
            </div>

            {/* tabela */}
            <div className="surface-card overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-secondary/40 text-muted-foreground">
                  <tr>
                    <th className="p-2 text-left">Produto</th>
                    <th className="p-2 text-left">Apresentação / Corte</th>
                    <th className="p-2 text-right whitespace-nowrap">Preço de venda
                      <InfoHint label="Preço de venda">
                        Valor final cobrado do cliente, após as regras oficiais, inclusive o piso de preço do
                        produto. Quando sobe pelo piso, a célula mostra um indicador com o preço cadastrado.
                      </InfoHint>
                    </th>
                    <th className="p-2 text-right">Custo
                      <InfoHint label="Custo">
                        Custo composto da apresentação (matéria-prima, merma, operacional e embalagem
                        compatíveis). Componente ausente aparece como “—” e nunca é somado como zero.
                      </InfoHint>
                    </th>
                    <th className="p-2 text-right whitespace-nowrap">Com. cliente
                      <InfoHint label="Comissão do cliente">
                        Comissão do cliente da negociação, aplicada sobre o preço de venda.
                      </InfoHint>
                    </th>
                    <th className="p-2 text-right whitespace-nowrap">Com. interm.
                      <InfoHint label="Comissão do intermediário">
                        Comissão do intermediário, sobre o preço de venda ou após a comissão do cliente,
                        conforme a base definida na negociação.
                      </InfoHint>
                    </th>
                    <th className="p-2 text-right">Impostos
                      <InfoHint label="Impostos">
                        Impostos/despesas percentuais e fixas por produto da negociação. Despesas por
                        pedido/entrega ficam fora da margem unitária (listadas à parte).
                      </InfoHint>
                    </th>
                    <th className="p-2 text-right">Resultado
                      <InfoHint label="Resultado">
                        Preço de venda menos custo, comissões e impostos. “Parcial” quando faltam fatores;
                        ausente (“—”) quando não há custo utilizável — nunca tratado como zero.
                      </InfoHint>
                    </th>
                    <th className="p-2 text-right">Margem
                      <InfoHint label="Margem">
                        Resultado dividido pelo preço de venda. Não se calcula margem quando o preço é zero.
                      </InfoHint>
                    </th>
                    <th className="p-2 text-left">Situação
                      <InfoHint label="Situação">
                        Acima da meta / Entre mín. e meta / Abaixo da mínima quando o cálculo é definitivo;
                        “Incompleto” quando falta algum fator (os motivos aparecem na linha).
                      </InfoHint>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => <Row key={i} r={r} />)}
                  {data.rows.length === 0 && (
                    <tr><td colSpan={10} className="p-6 text-center text-muted-foreground">Nenhuma combinação comercial encontrada.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* paginação */}
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{data.totalRows} combinação(ões) · página {data.page + 1} de {totalPages}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={data.page <= 0 || loading} onClick={() => void load(data.page - 1)}>Anterior</Button>
                <Button size="sm" variant="outline" disabled={data.page + 1 >= totalPages || loading} onClick={() => void load(data.page + 1)}>Próxima</Button>
              </div>
            </div>
          </>
        )}

        {!data && !loading && (
          <div className="surface-card p-12 text-center text-sm text-muted-foreground">
            <BarChart3 className="mx-auto mb-3 h-6 w-6 text-primary" />
            Selecione um cliente e clique em buscar para ver a composição de rentabilidade.
          </div>
        )}
      </div>
    </>
  );
}

// Detalhamento dos componentes conhecidos (para tooltip). Componente ausente (null) é omitido — nunca
// vira zero; zero cadastrado aparece como R$ 0,00. Cada valor acompanha a sua unidade.
function costBreakdown(r: ProfitabilityRow): string {
  const parts: string[] = [];
  if (r.rawCost != null) parts.push(`Matéria-prima: ${moneyWithUnit(r.rawCost, r.rawCostUnitName)}`);
  if (r.mermaSurcharge != null) parts.push(`Merma: ${moneyWithUnit(r.mermaSurcharge, r.rawCostUnitName)}`);
  if (r.operationalCost != null) parts.push(`Operacional: ${moneyWithUnit(r.operationalCost, r.rawCostUnitName)}`);
  if (r.packagingCost != null) parts.push(`Embalagem: ${moneyWithUnit(r.packagingCost, r.presentationUnitName)}`);
  return parts.join("\n");
}

// Célula "Custo" objetiva: um único valor (completo quando conclusivo; parcial quando faltam componentes).
// O detalhamento fica no tooltip. Sem custo utilizável → "—" (nunca zero). O marcador "parcial" evita que o
// valor seja lido como definitivo. Todos os valores vêm do backend (nenhum cálculo no frontend).
function CostCell({ r }: { r: ProfitabilityRow }) {
  const breakdown = costBreakdown(r);
  if (r.costValue == null) {
    return <span className="text-muted-foreground" title={breakdown || undefined}>—</span>;
  }
  return (
    <span title={breakdown || undefined} className={cn(!r.costComplete && "cursor-help underline decoration-dotted")}>
      {money(r.costValue)}
      {!r.costComplete && <span className="ml-1 text-[10px] font-normal text-muted-foreground">parcial</span>}
    </span>
  );
}

function Row({ r }: { r: ProfitabilityRow }) {
  return (
    <tr className={cn("border-t border-border align-top", !r.conclusive && "bg-secondary/20")}>
      <td className="p-2">
        <div className="font-medium">{r.productName}</div>
        {r.code != null && <div className="text-muted-foreground">Cód. {r.code}</div>}
      </td>
      <td className="p-2">{r.presentationUnitName}{r.cutName ? ` / ${r.cutName}` : " / Base"}</td>
      <td className="p-2 text-right tabular-nums whitespace-nowrap">
        {money(r.salePrice)}
        {r.priceFloorApplied && (
          <InfoHint label="Preço de venda (piso aplicado)">
            Preço cadastrado: <strong>{money(r.registeredPrice)}</strong>. O preço de venda subiu pelo piso do
            produto — nenhum corte fica abaixo do maior preço específico (por kg) cadastrado para o produto.
          </InfoHint>
        )}
      </td>
      <td className="p-2 text-right tabular-nums"><CostCell r={r} /></td>
      <td className="p-2 text-right tabular-nums">{money(r.clientCommissionValue)}</td>
      <td className="p-2 text-right tabular-nums">{money(r.intermediaryCommissionValue)}</td>
      <td className="p-2 text-right tabular-nums">{money(r.taxesUnitValue)}</td>
      <td className="p-2 text-right tabular-nums">
        {money(r.resultValue ?? r.partialResultValue)}
        {r.partial && <div className="text-[10px] text-muted-foreground">parcial</div>}
        {r.resultLabel === "ANTES_DESPESAS_PEDIDO_ENTREGA" && (
          <div className="text-[10px] text-muted-foreground">antes das despesas por pedido/entrega</div>
        )}
      </td>
      <td className="p-2 text-right tabular-nums">
        {marginPct(r.marginPercent ?? r.partialMarginPercent)}
        {r.partial && <div className="text-[10px] text-muted-foreground">parcial</div>}
      </td>
      <td className="p-2">
        {r.conclusive && r.marginStatus && r.resultLabel === "ANTES_DESPESAS_PEDIDO_ENTREGA" ? (
          // Preliminar: há despesas por pedido/entrega fora do unitário — não é rentabilidade final aprovada.
          <span className="rounded border border-border bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            Preliminar · {statusLabel[r.marginStatus]} (antes das despesas por pedido/entrega)
          </span>
        ) : r.conclusive && r.marginStatus ? (
          <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-bold uppercase", statusStyle[r.marginStatus])}>
            {statusLabel[r.marginStatus]}
          </span>
        ) : r.conclusive ? (
          <span className="text-muted-foreground">{r.resultLabel === "ANTES_DESPESAS_PEDIDO_ENTREGA" ? "preliminar · sem metas" : "sem metas"}</span>
        ) : (
          <div className="space-y-0.5">
            <span className="rounded bg-destructive/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-destructive">Incompleto</span>
            <ul className="list-inside list-disc text-[10px] text-muted-foreground">
              {r.reasons.map((code, i) => <li key={i}>{REASON_LABELS[code] ?? code}</li>)}
            </ul>
          </div>
        )}
      </td>
    </tr>
  );
}
